import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Me, Project, Member, Room } from '../lib/types';
import { navigate } from '../lib/router';

const ROLE: Record<string, string> = { owner: 'Sahip', admin: 'Yönetici', member: 'Üye', guest: 'Misafir' };

export function ProjectSettings({
  project,
  me,
  onOpenMenu,
  onChanged,
}: {
  project: Project;
  me: Me;
  onOpenMenu: () => void;
  onChanged: () => void;
}) {
  const [members, setMembers] = useState<Member[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [repos, setRepos] = useState<{ id: string; fullName: string; defaultBranch: string }[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [repo, setRepo] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const admin = project.role === 'owner' || project.role === 'admin';

  const load = async () => {
    setMembers(await api.get(`/v1/projects/${project.id}/members`));
    setRooms(await api.get(`/v1/projects/${project.id}/rooms`));
    setRepos(await api.get(`/v1/projects/${project.id}/repos`));
  };
  useEffect(() => void load(), [project.id]);

  async function addOrInvite() {
    setMsg(null);
    setInviteUrl(null);
    try {
      await api.post(`/v1/projects/${project.id}/members`, { email, role });
      setMsg('Mevcut kullanıcı projeye eklendi.');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'user_not_found_use_invite') {
        const inv = await api.post<{ acceptUrl: string }>('/v1/invites', {
          email,
          projectId: project.id,
          role,
          ttlHours: 24 * 14,
        });
        const googleOnly = !inv.acceptUrl.includes('/invite#');
        if (googleOnly) {
          setMsg(
            `${email} henüz giriş yapmamış. ${new URL(inv.acceptUrl).origin} adresinden bu e-postanın Google hesabıyla giriş yaptığında projeye otomatik eklenecek (14 gün geçerli). Ona yalnızca adresi göndermeniz yeterli.`,
          );
        } else {
          setInviteUrl(inv.acceptUrl);
          setMsg(
            'Bu e-postayla hesap yok; tek kullanımlık, 14 gün geçerli davet bağlantısı oluşturuldu. Bağlantıyı kişiye güvenli bir kanaldan iletin.',
          );
        }
      } else setMsg(e instanceof ApiError ? `İşlem yapılamadı (${e.code}).` : 'İşlem yapılamadı.');
    }
    setEmail('');
    await load();
  }

  return (
    <>
      <header className="topbar">
        <button className="btn small only-narrow" onClick={onOpenMenu}>
          Menü
        </button>
        <h1>{project.name} — proje ayarları</h1>
      </header>
      <div className="page">
        <div className="page-inner">
          <h2>Üyeler</h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>Rol</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td>
                      {m.displayName}
                      {m.id === me.id && ' (siz)'}
                    </td>
                    <td>
                      {admin && m.id !== me.id ? (
                        <select
                          className="select"
                          aria-label={`${m.displayName} rolü`}
                          value={m.role}
                          onChange={async (e) => {
                            try {
                              await api.patch(`/v1/projects/${project.id}/members/${m.id}`, {
                                role: e.target.value,
                              });
                            } catch (er) {
                              setMsg(
                                er instanceof ApiError ? `Rol değişmedi (${er.code}).` : 'Rol değişmedi.',
                              );
                            }
                            await load();
                          }}
                        >
                          {Object.entries(ROLE).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </select>
                      ) : (
                        ROLE[m.role]
                      )}
                    </td>
                    <td>
                      {(admin || m.id === me.id) && (
                        <button
                          className="btn small danger"
                          onClick={async () => {
                            if (
                              !confirm(
                                `${m.displayName} projeden çıkarılsın mı? Canlı bağlantıları hemen kesilir.`,
                              )
                            )
                              return;
                            try {
                              await api.del(`/v1/projects/${project.id}/members/${m.id}`);
                            } catch (er) {
                              setMsg(
                                er instanceof ApiError && er.code === 'last_owner'
                                  ? 'Son sahip çıkarılamaz.'
                                  : 'Çıkarılamadı.',
                              );
                            }
                            await load();
                          }}
                        >
                          {m.id === me.id ? 'Projeden ayrıl' : 'Çıkar'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {admin && (
            <section>
              <h3>Üye ekle veya davet et</h3>
              <form
                className="form-grid"
                onSubmit={(e) => {
                  e.preventDefault();
                  void addOrInvite();
                }}
              >
                <label className="field">
                  <span>E-posta</span>
                  <input
                    className="input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <label className="field">
                  <span>Rol</span>
                  <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
                    {Object.entries(ROLE)
                      .filter(([k]) => k !== 'owner' || project.role === 'owner')
                      .map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                  </select>
                </label>
                <div className="field">
                  <span>&nbsp;</span>
                  <button className="btn primary" type="submit">
                    Ekle / davet et
                  </button>
                </div>
              </form>
              {msg && (
                <p className="notice" role="status">
                  {msg}
                </p>
              )}
              {inviteUrl && <div className="secret-once">{inviteUrl}</div>}
            </section>
          )}

          <section>
            <h3>Odalar</h3>
            <ul className="list">
              {rooms.map((r) => (
                <li
                  key={r.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--s3)', flexWrap: 'wrap' }}
                >
                  <span>
                    {r.name} <span className="hint">({r.kind === 'private' ? 'özel' : 'proje odası'})</span>
                  </span>
                  {admin && (
                    <label
                      style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--s2)', alignItems: 'center' }}
                    >
                      <input
                        type="checkbox"
                        checked={r.aiEnabled}
                        onChange={async (e) => {
                          await api.patch(`/v1/rooms/${r.id}`, { aiEnabled: e.target.checked });
                          await load();
                          onChanged();
                        }}
                      />
                      AI çağrılarına izin ver
                    </label>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3>Git depoları</h3>
            <p className="hint">
              Görev dalları <code>cr/task-N</code> biçimindedir; commit ve PR'lar <code>CR-TASK-N</code> ile
              görevlere bağlanır (GitHub webhook'u yapılandırıldığında).
            </p>
            <ul className="list">
              {repos.map((r) => (
                <li key={r.id}>
                  {r.fullName} <span className="hint">({r.defaultBranch})</span>
                </li>
              ))}
            </ul>
            {admin && (
              <form
                style={{ display: 'flex', gap: 'var(--s2)', marginTop: 'var(--s2)' }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await api.post(`/v1/projects/${project.id}/repos`, { fullName: repo });
                    setRepo('');
                  } catch {
                    setMsg('Depo eklenemedi (biçim: sahip/depo).');
                  }
                  await load();
                }}
              >
                <input
                  className="input"
                  placeholder="sahip/depo"
                  aria-label="GitHub deposu"
                  value={repo}
                  onChange={(e) => setRepo(e.target.value)}
                />
                <button className="btn" type="submit">
                  Bağla
                </button>
              </form>
            )}
          </section>

          {project.role === 'owner' && (
            <section>
              <h3>Projeyi sil</h3>
              <p className="hint">
                Proje; odaları, mesajları ve dosyalarıyla birlikte tüm üyeler için hemen kaldırılır. Bekleyen
                davetler iptal edilir. Kullanım ve maliyet kayıtları muhasebe için saklanır.
              </p>
              <button
                className="btn danger"
                onClick={async () => {
                  if (!confirm(`“${project.name}” projesi herkes için silinsin mi? Bu işlem geri alınamaz.`))
                    return;
                  try {
                    await api.del(`/v1/projects/${project.id}`);
                    onChanged();
                    navigate('/');
                  } catch (er) {
                    setMsg(er instanceof ApiError ? `Silinemedi (${er.code}).` : 'Silinemedi.');
                  }
                }}
              >
                Projeyi sil
              </button>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
